import { BitmapLayer, LineLayer, ScatterplotLayer } from "@deck.gl/layers"
import type { MapboxOverlayProps } from "@deck.gl/mapbox"
import { MapboxOverlay } from "@deck.gl/mapbox"
import { Maximize, Minimize } from "lucide-react"
import type { MapMouseEvent } from "maplibre-gl"
// maplibre 6 can't find its worker inside a bundle, so hand Vite the worker
// explicitly. `?worker&url` (not `?url`) so the worker's shared chunk is
// bundled with it.
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"
import "maplibre-gl/dist/maplibre-gl.css"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Map,
  Marker,
  NavigationControl,
  useControl,
  useMap,
} from "react-map-gl/maplibre"

import {
  BASELINE_BOX_POSITION,
  type CoverageMode,
  KM_TO_MILES,
  SHOW_BASELINE_ZOOM,
} from "@/components/coverage-map/constants"
import {
  type SqiGrid,
  getServiceQualityRaw,
  getSqiGrid,
  getSqiScore,
} from "@/services/portalApi"
import type { Station } from "@/services/types"
import { isAmModelingStation, isNxStation } from "@/services/types"
import { haversineDistance } from "@/utils/haversine"

// Target ~100k cells per grid request — server caps at 250k.
const SQI_TARGET_CELLS = 100_000
// Cell size bounds in Web Mercator meters. The server snaps to a power-of-2
// zoom ladder so requests just need to land in roughly the right magnitude.
const SQI_MIN_RESOLUTION_M = 200
const SQI_MAX_RESOLUTION_M = 200_000
const SQI_REFETCH_DEBOUNCE_MS = 200

// Web Mercator meters at the equator: 1° longitude ≈ earthCircumference / 360.
const METERS_PER_DEG_LON_EQUATOR = (2 * Math.PI * 6378137) / 360

// pickSqiResolution returns a requested cell size in Web Mercator meters,
// sized so the raster has roughly SQI_TARGET_CELLS. The bbox span is
// converted from degrees to Mercator meters at the bbox-center latitude;
// the server then snaps the value to the nearest ladder zoom.
function pickSqiResolution(
  lonRange: number,
  latRange: number,
  centerLat: number,
): number {
  const cosLat = Math.cos((centerLat * Math.PI) / 180)
  const lonRangeM = lonRange * METERS_PER_DEG_LON_EQUATOR
  // Latitude span in Mercator meters varies with cos(lat) — at the equator
  // 1° lat ≈ 1° lon; at 60° latitude a 1° lat span ≈ 2× the projected meters
  // of a 1° lon span. Approximate using cos at the center latitude.
  const latRangeM =
    (latRange * METERS_PER_DEG_LON_EQUATOR) / Math.max(cosLat, 0.01)
  const res = Math.sqrt((lonRangeM * latRangeM) / SQI_TARGET_CELLS)
  return Math.min(SQI_MAX_RESOLUTION_M, Math.max(SQI_MIN_RESOLUTION_M, res))
}

// SQI color ramp hardcoded from tuned anchors.
// Stops are keyed by score to preserve intentional boundary shifts at
// 45→55 and 73→77.
const SQI_RAMP_STOPS: Array<{ score: number; rgb: [number, number, number] }> =
  [
    { score: 0, rgb: [51, 100, 126] }, // #33647e
    { score: 45, rgb: [46, 150, 255] }, // #2e96ff
    { score: 55, rgb: [254, 255, 168] }, // #feffa8
    { score: 73, rgb: [255, 234, 0] }, // #ffea00
    { score: 77, rgb: [255, 201, 102] }, // #ffc966
    { score: 100, rgb: [255, 131, 0] }, // #ff8300
  ]

// sqiRamp returns the [r, g, b] color for an SQI fraction t ∈ [0, 1].
function sqiRamp(t: number): [number, number, number] {
  const score = Math.max(0, Math.min(100, t * 100))
  if (score <= SQI_RAMP_STOPS[0].score) return SQI_RAMP_STOPS[0].rgb
  if (score >= SQI_RAMP_STOPS[SQI_RAMP_STOPS.length - 1].score) {
    return SQI_RAMP_STOPS[SQI_RAMP_STOPS.length - 1].rgb
  }

  let lo = 0
  for (let i = 0; i < SQI_RAMP_STOPS.length - 1; i++) {
    if (score <= SQI_RAMP_STOPS[i + 1].score) {
      lo = i
      break
    }
  }

  const a = SQI_RAMP_STOPS[lo]
  const b = SQI_RAMP_STOPS[lo + 1]
  const range = Math.max(1, b.score - a.score)
  const f = (score - a.score) / range
  return [
    Math.round(a.rgb[0] + (b.rgb[0] - a.rgb[0]) * f),
    Math.round(a.rgb[1] + (b.rgb[1] - a.rgb[1]) * f),
    Math.round(a.rgb[2] + (b.rgb[2] - a.rgb[2]) * f),
  ]
}

// CSS linear-gradient string for the vertical legend bar (bottom = 0, top = 100).
// Anchored at the same score stops as the lookup.
const SQI_GRADIENT_CSS = `linear-gradient(to top, ${SQI_RAMP_STOPS.map(
  ({ score, rgb: [r, g, b] }) => {
    const pct = score
    return `rgb(${r},${g},${b}) ${pct}%`
  },
).join(", ")})`

// Map a 0-100 score to an RGBA byte triplet via Cividis. Cells with v < 0
// (the -1 "no coverage" sentinel from the server — outside any AM/healthy
// station's reach) are drawn transparent so the basemap shows through
// uncolored. v == 0 is a legitimate worst-valid score and gets colored.
function sqiToRgba(
  v: number,
  out: Uint8ClampedArray,
  off: number,
  minVisibleScoreInclusive: number,
) {
  if (v < 0 || v < minVisibleScoreInclusive) {
    out[off + 3] = 0
    return
  }
  const [r, g, b] = sqiRamp(Math.max(0, Math.min(1, v / 100)))
  out[off] = r
  out[off + 1] = g
  out[off + 2] = b
  out[off + 3] = 115 // ~0.45 alpha — let the basemap show through clearly.
}

// Station-kind marker icons. Rendered as plain JSX SVG inside a DOM <Marker>
// (no canvas/WebGL rasterization involved, so there's no need for a data-URI
// round-trip). The colors intentionally match the legend swatches.
//   AM-only       teal triangle  rgb(20,184,166)  Tailwind teal-500
//   NX-only       orange square  rgb(249,115,22)  Tailwind orange-500
//   NX + AM       violet circle  rgb(124,58,237)  Tailwind violet-600
const ICON_SIZE = 24

type StationIconKind = "am-only" | "nx-only" | "nx-and-am"

const StationIcon = ({ kind }: { kind: StationIconKind }) => {
  switch (kind) {
    case "am-only":
      return (
        <svg
          width={ICON_SIZE}
          height={ICON_SIZE}
          viewBox="0 0 24 24"
          className="block pointer-events-none"
        >
          <polygon
            points="12,3 22,21 2,21"
            fill="rgb(20,184,166)"
            stroke="white"
            strokeWidth={1.5}
            strokeLinejoin="round"
          />
        </svg>
      )
    case "nx-only":
      return (
        <svg
          width={ICON_SIZE}
          height={ICON_SIZE}
          viewBox="0 0 24 24"
          className="block pointer-events-none"
        >
          <rect
            x={4}
            y={4}
            width={16}
            height={16}
            fill="rgb(249,115,22)"
            stroke="white"
            strokeWidth={1.5}
          />
        </svg>
      )
    case "nx-and-am":
      return (
        <svg
          width={ICON_SIZE}
          height={ICON_SIZE}
          viewBox="0 0 24 24"
          className="block pointer-events-none"
        >
          <circle
            cx={12}
            cy={12}
            r={9}
            fill="rgb(124,58,237)"
            stroke="white"
            strokeWidth={1.5}
          />
        </svg>
      )
  }
}

// AM-input selection: cutoff matches the backend's AM-input picker (service_quality.go),
// but we cap the number of rendered stations for UI/debug readability.
const AM_MODEL_SELECTION_CUTOFF_KM = 250
const AM_MODEL_MAX_STATIONS = 4
// Minimum number of nominally-healthy NX stations to walk nearest-first for the
// SQI debug overlay, mirroring the backend rule that needs at least 2 healthy NX
// stations. "Nominally healthy" is the rolling-average gate SQI selects on.
const SQI_MIN_HEALTHY_NX = 2

// pickAmInputStations returns up to AM_MODEL_MAX_STATIONS nearest *nominally
// healthy* AM-input stations within AM_MODEL_SELECTION_CUTOFF_KM of (lat, lon).
// Gates on nominal (rolling-average) health because that is what the backend
// selects on; note the backend may use more stations — this is a UI/debug cap.
function pickAmInputStations(
  stations: Array<Station>,
  lat: number,
  lon: number,
): Array<{ station: Station; distKm: number }> {
  const cands: Array<{ station: Station; distKm: number }> = []
  for (const s of stations) {
    if (!isAmModelingStation(s)) continue
    if (!s.nominallyHealthy) continue
    const d = haversineDistance(s.lat, s.lon, lat, lon)
    if (d > AM_MODEL_SELECTION_CUTOFF_KM) continue
    cands.push({ station: s, distKm: d })
  }
  cands.sort((a, b) => a.distKm - b.distKm)
  return cands.slice(0, AM_MODEL_MAX_STATIONS)
}

// walkNxStationsUntilHealthy walks stations sorted by distance to (lat, lon)
// and returns the prefix needed to include at least minHealthy *nominally
// healthy* NX stations. Filters to NX-tagged stations (kind bit 0) only. If the
// full set doesn't contain enough nominally-healthy NX stations, returns
// everything we have. Nominal health is the rolling-average gate SQI selects on.
function walkNxStationsUntilHealthy(
  stations: Array<Station>,
  lat: number,
  lon: number,
  minHealthy: number,
): Array<{ station: Station; distKm: number }> {
  const nx = stations
    .filter((s) => isNxStation(s))
    .map((s) => ({
      station: s,
      distKm: haversineDistance(s.lat, s.lon, lat, lon),
    }))
    .sort((a, b) => a.distKm - b.distKm)

  const picked: Array<{ station: Station; distKm: number }> = []
  let healthy = 0
  for (const entry of nx) {
    picked.push(entry)
    if (entry.station.nominallyHealthy) healthy++
    if (healthy >= minHealthy) break
  }
  return picked
}

// Point-wise SQI is fetched from /service_quality (precise per-point evaluation),
// not sampled from the heatmap raster — sampling the raster would let the badge
// value drift when the user zooms (because the lattice resolution changes), and
// the user expects a clicked point to keep its score until they click again.

// sqiToCssColor returns a CSS color string matching the heatmap ramp.
function sqiToCssColor(v: number): string {
  if (v < 0) return "transparent"
  const [r, g, b] = sqiRamp(Math.max(0, Math.min(1, v / 100)))
  return `rgb(${r}, ${g}, ${b})`
}

// DeckGL Overlay component that seamlessly hooks into react-map-gl
function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props))

  useEffect(() => {
    // During rapid conditional mount/unmount (e.g. toggling SQI/baseline),
    // deck.gl may receive props while detached from maplibre. Guarding on
    // the attached map avoids transient getProjection() null errors.
    const attachedMap = (overlay as unknown as { _map?: unknown })._map
    if (!attachedMap) return
    try {
      overlay.setProps(props)
    } catch (err) {
      // Deck/maplibre can briefly desynchronize during rapid overlay toggles.
      // Ignore this frame; the next render/effect re-applies props.
      console.warn("[deck] setProps skipped during transient map state", err)
    }
  }, [overlay, props.layers, props.style])

  return null
}

// Component to create baseline label with zoom-aware visibility
const BaselineLabel = ({
  start,
  end,
  distance,
  isMetric,
}: {
  start: [number, number]
  end: [number, number]
  distance: number
  isMetric: boolean
}) => {
  const { current: map } = useMap()
  const [currentZoom, setCurrentZoom] = useState(map ? map.getZoom() : 0)

  useEffect(() => {
    if (!map) return
    const onZoom = () => setCurrentZoom(map.getZoom())
    map.on("zoom", onZoom)
    setCurrentZoom(map.getZoom())
    return () => {
      map.off("zoom", onZoom)
    }
  }, [map])

  const boxLat =
    BASELINE_BOX_POSITION * start[0] + (1 - BASELINE_BOX_POSITION) * end[0]
  const boxLon =
    BASELINE_BOX_POSITION * start[1] + (1 - BASELINE_BOX_POSITION) * end[1]

  const formattedDistance = isMetric
    ? `${distance.toFixed(2)} km`
    : `${(distance * KM_TO_MILES).toFixed(2)} mi`

  // Only show the box if zoom is high enough
  if (currentZoom < SHOW_BASELINE_ZOOM) {
    return null
  }

  return (
    <Marker
      longitude={boxLon}
      latitude={boxLat}
      anchor="center"
      style={{ zIndex: 10 }}
    >
      <div
        className="baseline-label"
        style={{
          background: "white",
          border: "1px solid black",
          borderRadius: "6px",
          fontSize: "11px",
          fontWeight: 400,
          padding: "2px 4px",
          whiteSpace: "nowrap",
          textAlign: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
      >
        {formattedDistance}
      </div>
    </Marker>
  )
}

interface MapContentProps {
  stations: Array<Station>
  searchLocation: { lat: number; lon: number } | null
  nearestStations: Array<{ dist: number; index: number; healthy: boolean }>
  onMapClick: (lat: number, lon: number) => void
  loading: boolean
  isMetric: boolean
  showCoverage: boolean
  initialCenter: [number, number]
  initialZoom: number
  minZoom: number
  maxZoom: number
  coverageRadius: number
  mapType: "light" | "dark"
  flyToLocation?: { lat: number; lon: number; zoom: number; id: number } | null
  flyDuration: number
  containerRef?: React.RefObject<HTMLDivElement>
  coverageMode: CoverageMode
  debugMode: boolean
  // ISO 8601 timestamp from the X-Stations-Gathered-At response header, or
  // null if the backend didn't surface one. Rendered in the SQI legend.
  stationsGatheredAt: string | null
}

const FullscreenControl = ({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const mapContainer = containerRef.current

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
  }, [])

  const toggleFullscreen = async () => {
    try {
      if (!mapContainer) return

      if (!document.fullscreenElement) {
        await mapContainer.requestFullscreen()
      } else {
        await document.exitFullscreen()
      }
    } catch (err) {
      console.error("Error toggling fullscreen:", err)
    }
  }

  return (
    <button
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        toggleFullscreen()
      }}
      className="maplibregl-ctrl-icon"
      title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
      aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isFullscreen ? (
        <Minimize size={16} strokeWidth={2.5} />
      ) : (
        <Maximize size={16} strokeWidth={2.5} />
      )}
    </button>
  )
}

const MapContent: React.FC<MapContentProps> = ({
  stations,
  searchLocation,
  nearestStations,
  onMapClick,
  loading,
  isMetric,
  showCoverage,
  initialCenter,
  initialZoom,
  minZoom,
  maxZoom,
  coverageRadius,
  mapType,
  flyToLocation,
  flyDuration,
  containerRef,
  coverageMode,
  debugMode,
  stationsGatheredAt,
}) => {
  const localContainerRef = useRef<HTMLDivElement>(null)
  const activeContainerRef = containerRef || localContainerRef
  const mapRef = useRef<any>(null)
  const [isFlying, setIsFlying] = useState(false)
  // Tracks whether the maplibre instance has emitted `load`. The SQI fetch
  // effect needs this so it can run on first mount (the ref isn't set yet
  // during the first render pass); without it the heatmap only appeared
  // after the user toggled the coverage layer dropdown.
  const [mapReady, setMapReady] = useState(false)
  const [sqiGrid, setSqiGrid] = useState<SqiGrid | null>(null)
  const [sqiLoading, setSqiLoading] = useState(false)
  const [sqiError, setSqiError] = useState<string | null>(null)
  // Identity of the current station batch, used below as the dep that drives
  // automatic SQI refreshes. The gathered-at timestamp is the precise signal,
  // but the backend sets its header best-effort (partnerservice main.go skips
  // it when the worker's write time is unreadable), so a null timestamp is
  // reachable and would never change across polls. Falling back to the
  // stations array's identity keeps the raster in step with the markers in
  // that degraded case: react-query hands back a new array only when the batch
  // actually differs, so an unchanged poll still does not refetch.
  const stationBatch = stationsGatheredAt ?? stations

  const sqiFetchToken = useRef(0)
  const sqiAbortRef = useRef<AbortController | null>(null)

  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      onMapClick(e.lngLat.lat, e.lngLat.lng)
    },
    [onMapClick],
  )

  useEffect(() => {
    const handleFullscreenChange = () => {
      /*
      /* Use nested requestAnimationFrame x2 intentionally to wait for the next paint cycle
      /* This ensures the DOM has fully updated before resizing
      */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (mapRef.current) {
            mapRef.current.resize()
          }
        })
      })
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)

    // Window resize: maplibre needs an explicit resize() to keep its WebGL
    // canvas dimensions in sync with the container, otherwise the basemap
    // (and any deck.gl overlays like the SQI BitmapLayer) projects to the
    // wrong pixels.
    const handleWindowResize = () => {
      requestAnimationFrame(() => {
        if (mapRef.current) mapRef.current.resize()
      })
    }
    window.addEventListener("resize", handleWindowResize)

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
      window.removeEventListener("resize", handleWindowResize)
    }
  }, [])

  // SQI grid fetch: re-runs when entering SQI mode and on subsequent map moves.
  useEffect(() => {
    if (coverageMode !== "sqi" || !mapRef.current || !mapReady) {
      setSqiError(null)
      return
    }
    const map = mapRef.current
    let debounce: ReturnType<typeof setTimeout> | null = null

    const fetchGrid = async () => {
      const b = map.getBounds?.()
      if (!b) {
        return
      }
      const minLat = Math.max(-90, b.getSouth())
      const maxLat = Math.min(90, b.getNorth())
      let minLon = b.getWest()
      let maxLon = b.getEast()
      if (minLon < -180) minLon = -180
      if (maxLon > 180) maxLon = 180
      if (maxLat <= minLat || maxLon <= minLon) {
        return
      }
      const resolution = pickSqiResolution(
        maxLon - minLon,
        maxLat - minLat,
        (minLat + maxLat) / 2,
      )
      const token = ++sqiFetchToken.current

      // Cancel the previous fetch — its computation on the backend is now
      // wasted CPU, and stacking requests was causing nginx 502s.
      sqiAbortRef.current?.abort()
      const ac = new AbortController()
      sqiAbortRef.current = ac

      setSqiLoading(true)
      setSqiError(null)
      try {
        const grid = await getSqiGrid(
          {
            min_lat: minLat,
            max_lat: maxLat,
            min_lon: minLon,
            max_lon: maxLon,
            resolution_m: resolution,
          },
          ac.signal,
        )
        if (token !== sqiFetchToken.current) {
          return
        }
        setSqiGrid(grid)
      } catch (err: any) {
        // Cancelled requests are noise — the next fetch superseded this one
        // (debounced refire on pan/zoom/resize). Surface, don't error.
        const canceled =
          err?.code === "ERR_CANCELED" ||
          err?.name === "CanceledError" ||
          err?.name === "AbortError" ||
          err?.message === "canceled"
        if (token !== sqiFetchToken.current || canceled) {
          return
        }
        console.error("[sqi] fetch failed", {
          token,
          name: err?.name,
          code: err?.code,
          message: err?.message,
          status: err?.response?.status,
          statusText: err?.response?.statusText,
          data: err?.response?.data,
          stack: err?.stack,
        })
        // Deliberately leave sqiGrid alone. A failed fetch is usually a
        // transient blip on a refetch (a pan, or a new station batch), and
        // clearing the grid would blank the heatmap mid-view; the stale raster
        // is far better than an empty map. sqiError still surfaces the failure,
        // and the next moveend or station poll retries.
        setSqiError(err?.message ?? "Failed to load SQI grid")
      } finally {
        if (token === sqiFetchToken.current) setSqiLoading(false)
      }
    }

    const scheduleFetch = () => {
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(fetchGrid, SQI_REFETCH_DEBOUNCE_MS)
    }

    fetchGrid()
    map.on("moveend", scheduleFetch)
    map.on("resize", scheduleFetch)
    return () => {
      map.off("moveend", scheduleFetch)
      map.off("resize", scheduleFetch)
      if (debounce) clearTimeout(debounce)
      sqiAbortRef.current?.abort()
      sqiAbortRef.current = null
    }
    // stationBatch is what makes the SQI raster refresh on its own, with no
    // pan or zoom from the user. The chain: the stations query polls on its
    // own timer -> a new batch changes stationBatch -> that changes this
    // effect's deps -> the effect re-runs and calls fetchGrid() for the
    // current viewport. So the timer driving the station list is also,
    // indirectly, the timer driving the SQI overlay; the map events below only
    // handle refetches caused by the user moving the viewport.
    //
    // stationBatch is a dependency, not a value this effect reads. It matters
    // because the SQI grid is derived from the same stations as the markers:
    // without it the raster would keep showing the batch that was current when
    // the user last panned, next to markers that had already moved on. The
    // token/abort machinery above makes the resulting refetch safe against one
    // already in flight, and the old raster stays on screen until the new one
    // resolves.
  }, [coverageMode, mapReady, stationBatch])

  // Debug overlay: toggled by the settings-panel Debug Mode switch. When on,
  // every click in SQI mode triggers a
  // /portal-api/v1/service_quality call and renders the full JSON response
  // in a small scrollable overlay.
  const [debugJson, setDebugJson] = useState<unknown>(null)
  const [debugError, setDebugError] = useState<string | null>(null)
  const [debugLoading, setDebugLoading] = useState(false)
  const [sqiDebugClipThreshold, setSqiDebugClipThreshold] = useState(0)

  // Depend on the primitive lat/lon, not the searchLocation object reference.
  // The route's nearestStations effect re-creates searchLocation on every
  // render even when lat/lon are unchanged, which would re-fire this effect
  // and prevent the loading state from ever settling.
  const debugLat = searchLocation?.lat
  const debugLon = searchLocation?.lon
  useEffect(() => {
    if (
      !debugMode ||
      debugLat === undefined ||
      debugLon === undefined ||
      coverageMode !== "sqi"
    ) {
      setDebugJson(null)
      setDebugError(null)
      setDebugLoading(false)
      return
    }
    let cancelled = false
    setDebugLoading(true)
    setDebugError(null)
    getServiceQualityRaw(debugLat, debugLon, "extended")
      .then((j) => {
        if (cancelled) return
        setDebugJson(j)
      })
      .catch((err: any) => {
        if (cancelled) return
        setDebugError(err?.response?.data?.error ?? err?.message ?? "failed")
        setDebugJson(null)
      })
      .finally(() => {
        if (!cancelled) setDebugLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [debugMode, debugLat, debugLon, coverageMode])

  // SQI value at the user's click / search location, from a precise per-point
  // evaluation via /service_quality. Keyed on the lat/lon primitives (and
  // coverage mode) — *not* on the heatmap grid — so the badge value is locked
  // in at click time and doesn't drift when the user zooms the map. null
  // before the first fetch resolves, or when the score is unavailable.
  const [sqiAtSearch, setSqiAtSearch] = useState<number | null>(null)
  useEffect(() => {
    if (
      coverageMode !== "sqi" ||
      debugLat === undefined ||
      debugLon === undefined
    ) {
      setSqiAtSearch(null)
      return
    }
    let cancelled = false
    getSqiScore(debugLat, debugLon)
      .then((resp) => {
        if (cancelled) return
        const raw = resp.sqi
        if (typeof raw !== "number" || raw < 0) {
          setSqiAtSearch(null)
          return
        }
        setSqiAtSearch(Math.round(raw))
      })
      .catch(() => {
        if (!cancelled) setSqiAtSearch(null)
      })
    return () => {
      cancelled = true
    }
  }, [debugLat, debugLon, coverageMode])

  // SQI-mode visible stations on click. Two independent picks, merged on name:
  //   1. NX stations walked nearest-first until at least 2 nominally healthy are
  //      included.
  //   2. All AM-input stations the backend would pick for q_atmo_model.
  // Distance is the click-point distance; we keep the min if a station appears
  // in both sets. Stations that aren't nominally healthy (both NX and AM-input
  // flavors) are filtered out — the overlay draws the stations SQI actually
  // selects, which gates on nominal (rolling-average) health, not the
  // instantaneous Healthy bit.
  const sqiVisibleStations = useMemo(() => {
    if (coverageMode !== "sqi" || !searchLocation) return []
    const nx = walkNxStationsUntilHealthy(
      stations,
      searchLocation.lat,
      searchLocation.lon,
      SQI_MIN_HEALTHY_NX,
    )
    const am = pickAmInputStations(
      stations,
      searchLocation.lat,
      searchLocation.lon,
    )
    // NOTE: `Map` is shadowed by the react-map-gl import at the top of this
    // file, so we use a plain Record keyed by lat/lon (station Name isn't
    // exposed in the public /stations JSON).
    // The value type carries `| undefined` because tsconfig leaves
    // noUncheckedIndexedAccess off: without it a lookup on a key that is not
    // there yet still types as present, and the !existing branch below -- the
    // one that seeds each new key -- reads as dead code.
    type Nearest = { station: Station; distKm: number }
    const byKey: Record<string, Nearest | undefined> = {}
    for (const e of [...nx, ...am]) {
      if (!e.station.nominallyHealthy) continue
      const key = `${e.station.lat.toFixed(6)},${e.station.lon.toFixed(6)}`
      const existing = byKey[key]
      if (!existing || e.distKm < existing.distKm) byKey[key] = e
    }
    return Object.values(byKey)
      .filter((e): e is Nearest => e !== undefined)
      .sort((a, b) => a.distKm - b.distKm)
  }, [stations, searchLocation, coverageMode])

  // Convert the SQI grid to an RGBA ImageData buffer, flipping Y since BitmapLayer
  // expects row 0 = north edge while the server returns row 0 = south (min_lat).
  const sqiImage = useMemo(() => {
    if (!sqiGrid) return null
    const { width, height, values } = sqiGrid
    if (!width || !height) return null
    const minVisibleScoreInclusive =
      debugMode && coverageMode === "sqi" ? sqiDebugClipThreshold : -1
    const buf = new Uint8ClampedArray(width * height * 4)
    for (let row = 0; row < height; row++) {
      const srcRow = (height - 1 - row) * width
      const dstRow = row * width
      for (let col = 0; col < width; col++) {
        sqiToRgba(
          values[srcRow + col],
          buf,
          (dstRow + col) * 4,
          minVisibleScoreInclusive,
        )
      }
    }
    return new ImageData(buf, width, height)
  }, [coverageMode, debugMode, sqiDebugClipThreshold, sqiGrid])

  const baselineCoverageLayers = useMemo(() => {
    if (!(showCoverage && coverageMode === "baseline")) return []
    return [
      new ScatterplotLayer({
        id: "coverage-circles",
        data: stations,
        getPosition: (d) => [d.lon, d.lat],
        getRadius: coverageRadius * 1000,
        getFillColor: [70, 167, 221, 255],
        radiusUnits: "meters",
        pickable: false,
        antialiasing: true,
      }),
    ]
  }, [showCoverage, coverageMode, stations, coverageRadius])

  const sqiHeatmapLayers = useMemo(() => {
    if (!(showCoverage && coverageMode === "sqi" && sqiImage && sqiGrid)) {
      return []
    }
    return [
      new BitmapLayer({
        id: "sqi-heatmap",
        bounds: [
          sqiGrid.min_lon,
          sqiGrid.min_lat,
          sqiGrid.max_lon,
          sqiGrid.max_lat,
        ],
        image: sqiImage,
        opacity: 1,
        pickable: false,
      }),
    ]
  }, [showCoverage, coverageMode, sqiImage, sqiGrid])

  const stationLineLayers = useMemo(() => {
    if (
      coverageMode === "baseline" &&
      searchLocation &&
      !isFlying &&
      nearestStations.length > 0
    ) {
      return [
        new LineLayer({
          id: "nearest-lines",
          data: nearestStations,
          getSourcePosition: () => [searchLocation.lon, searchLocation.lat],
          getTargetPosition: (d) => [
            stations[d.index].lon,
            stations[d.index].lat,
          ],
          getColor: mapType === "dark" ? [255, 255, 255, 204] : [0, 0, 0, 204],
          getWidth: 1,
        }),
      ]
    }

    if (
      debugMode &&
      coverageMode === "sqi" &&
      searchLocation &&
      !isFlying &&
      sqiVisibleStations.length > 0
    ) {
      return [
        new LineLayer({
          id: "sqi-visible-lines",
          data: sqiVisibleStations,
          getSourcePosition: () => [searchLocation.lon, searchLocation.lat],
          getTargetPosition: (d) => [d.station.lon, d.station.lat],
          getColor: mapType === "dark" ? [255, 255, 255, 204] : [0, 0, 0, 204],
          getWidth: 1,
        }),
      ]
    }

    return []
  }, [
    coverageMode,
    debugMode,
    isFlying,
    mapType,
    nearestStations,
    searchLocation,
    sqiVisibleStations,
    stations,
  ])

  useEffect(() => {
    if (flyToLocation && mapRef.current) {
      setIsFlying(true)
      mapRef.current.flyTo({
        center: [flyToLocation.lon, flyToLocation.lat],
        zoom: flyToLocation.zoom,
        duration: flyDuration,
      })

      const onMoveEnd = () => setIsFlying(false)
      mapRef.current.once("moveend", onMoveEnd)
    }
  }, [flyToLocation])

  return (
    <div
      ref={containerRef ? undefined : localContainerRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      className="bg-gray-50 overflow-hidden"
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm z-[2000] transition-opacity duration-300">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-600 rounded-full animate-spin" />
            <div className="text-sm font-semibold text-gray-600 animate-pulse uppercase tracking-widest">
              Initializing Map Data...
            </div>
          </div>
        </div>
      )}

      <Map
        ref={mapRef}
        workerUrl={maplibreWorkerUrl}
        initialViewState={{
          longitude: initialCenter[1],
          latitude: initialCenter[0],
          zoom: initialZoom,
        }}
        mapStyle={
          mapType === "dark"
            ? `https://tiles.stadiamaps.com/styles/alidade_smooth_dark.json?api_key=${import.meta.env.VITE_STADIA_MAPS_API_KEY}`
            : `https://tiles.stadiamaps.com/styles/alidade_smooth.json?api_key=${import.meta.env.VITE_STADIA_MAPS_API_KEY}`
        }
        style={{ width: "100%", height: "100%" }}
        minZoom={minZoom}
        maxZoom={maxZoom}
        onClick={handleMapClick}
        onLoad={(e) => {
          setMapReady(true)
          // Keep pinch-zoom enabled on touch devices, but disable rotation.
          //
          // Optional-chained on purpose. maplibre types touchZoomRotate as
          // always present, but the handler runs against whatever `target` the
          // caller hands it, the chain costs nothing, and throwing here
          // unmounts the map inside a React event handler.
          e.target.touchZoomRotate?.enable?.()
          e.target.touchZoomRotate?.disableRotation?.()
        }}
        attributionControl={false}
        dragRotate={false}
        doubleClickZoom={false}
        touchPitch={false}
        renderWorldCopies={false}
      >
        <style>
          {`
            .maplibregl-ctrl-bottom-right {
              z-index: 20 !important;
            }
          `}
        </style>
        <NavigationControl
          position="bottom-right"
          showCompass={false}
          style={{ marginRight: "20px", marginBottom: "20px" }}
        />

        {/* Custom Attribution Bar */}
        <div className="absolute bottom-0 right-0 bg-white/90 text-[11px] leading-[1.2] px-2 py-[1px] pointer-events-auto z-10 flex items-center gap-1 font-sans text-gray-800">
          <span>
            &copy;{" "}
            <a
              href="https://stadiamaps.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0078A8] hover:underline"
            >
              Stadia Maps
            </a>
          </span>
          <span>
            &copy;{" "}
            <a
              href="https://openmaptiles.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0078A8] hover:underline"
            >
              OpenMapTiles
            </a>
          </span>
          <span>
            &copy;{" "}
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0078A8] hover:underline"
            >
              OpenStreetMap
            </a>
          </span>
        </div>

        <div
          className="maplibregl-ctrl maplibregl-ctrl-group"
          style={{
            position: "absolute",
            bottom: "88px",
            right: "20px",
            zIndex: 20,
          }}
        >
          <FullscreenControl containerRef={activeContainerRef} />
        </div>

        {mapReady && (
          <>
            <DeckGLOverlay
              layers={baselineCoverageLayers}
              style={{ opacity: "0.5", zIndex: "1" }}
            />
            <DeckGLOverlay layers={sqiHeatmapLayers} style={{ zIndex: "1" }} />
            <DeckGLOverlay layers={stationLineLayers} style={{ zIndex: "8" }} />
          </>
        )}

        {showCoverage && coverageMode === "sqi" && (sqiLoading || sqiError) && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1100] bg-white/90 border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 shadow-md">
            {sqiLoading ? "Loading SQI heatmap…" : `SQI error: ${sqiError}`}
          </div>
        )}

        {/* Legend (top-right).
            - Station-marker key is always rendered — markers themselves are
              visible in every mode.
            - SQI ramp + "Data as of …" only appear when the heatmap is on. */}
        <div className="absolute top-4 right-4 z-[1100] bg-white/95 border border-gray-200 rounded-lg shadow-md px-3 py-2 text-[11px] font-semibold text-gray-700 select-none">
          {coverageMode === "sqi" && (
            <div className="mb-1">
              <div className="mb-1 uppercase tracking-wider text-gray-500">
                Service Quality
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3.5 h-[100px] rounded-[3px] overflow-hidden"
                  style={{ background: SQI_GRADIENT_CSS }}
                />
                <div className="flex flex-col justify-between h-[100px]">
                  <span>100</span>
                  <span>50</span>
                  <span>0</span>
                </div>
              </div>
            </div>
          )}

          {/* In SQI mode the normal portal view shows only the SQI score badge;
              the nearest-stations key is a debugging aid and only renders when
              Debug Mode is enabled. Baseline mode always shows it. */}
          {(coverageMode !== "sqi" || debugMode) && (
            <div>
              <div className="uppercase tracking-wider text-gray-500 mb-1">
                Nearest stations
              </div>
              {coverageMode === "baseline" ? (
                <>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      aria-hidden
                      className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 border-[1.5px] border-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
                    />
                    <span>Healthy</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 border-[1.5px] border-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
                    />
                    <span>Unhealthy</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className="inline-flex w-3.5 h-3.5 items-center justify-center"
                      aria-hidden
                    >
                      <svg viewBox="0 0 24 24" className="w-3 h-3">
                        <polygon
                          points="12,3 22,21 2,21"
                          fill="rgb(20,184,166)"
                        />
                      </svg>
                    </span>
                    <span>AM modeling only</span>
                  </div>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className="inline-flex w-3.5 h-3.5 items-center justify-center"
                      aria-hidden
                    >
                      <svg viewBox="0 0 24 24" className="w-3 h-3">
                        <rect
                          x="4"
                          y="4"
                          width="16"
                          height="16"
                          fill="rgb(249,115,22)"
                        />
                      </svg>
                    </span>
                    <span>NX anchor only</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-flex w-3.5 h-3.5 items-center justify-center"
                      aria-hidden
                    >
                      <svg viewBox="0 0 24 24" className="w-3 h-3">
                        <circle cx="12" cy="12" r="9" fill="rgb(124,58,237)" />
                      </svg>
                    </span>
                    <span>NX anchor + AM modeling</span>
                  </div>
                </>
              )}
            </div>
          )}

          {stationsGatheredAt && (
            <div className="mt-1 pt-0.5 text-[10px] text-gray-500 font-normal leading-tight">
              {coverageMode === "sqi" && (
                <div className="mb-1">
                  <a
                    href="https://www.swiftnav.com/service-quality-indicator"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-sm text-swift-orange hover:text-swift-orange/90 hover:underline"
                  >
                    Learn More
                  </a>
                </div>
              )}
              <div
                className="uppercase tracking-wider text-gray-400"
                title={
                  debugMode
                    ? `Station-health data gathered ${stationsGatheredAt} (UTC).`
                    : undefined
                }
              >
                Data as of
              </div>
              <div>
                {new Date(stationsGatheredAt).toLocaleString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          )}
        </div>

        {/* Baseline mode: slider-driven N nearest stations as red/green dots. */}
        {coverageMode === "baseline" &&
          searchLocation &&
          !isFlying &&
          nearestStations.length > 0 && (
            <>
              {nearestStations.map((nearest, idx) => {
                const station = stations[nearest.index]
                return (
                  <React.Fragment key={idx}>
                    <Marker
                      longitude={station.lon}
                      latitude={station.lat}
                      anchor="center"
                      style={{ zIndex: 10 }}
                    >
                      <div
                        className={`w-3 h-3 rounded-full border-2 border-white box-border ${
                          station.healthy ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                    </Marker>
                    <BaselineLabel
                      start={[searchLocation.lat, searchLocation.lon]}
                      end={[station.lat, station.lon]}
                      distance={nearest.dist}
                      isMetric={isMetric}
                    />
                  </React.Fragment>
                )
              })}
            </>
          )}

        {/* SQI mode: union of (NX walked until 2 healthy) + (all q_atmo_model
            picks). Markers are kind-icons; connecting lines colored to the map theme.
          Only rendered with Debug Mode enabled — the normal SQI view shows just the score badge. */}
        {debugMode &&
          coverageMode === "sqi" &&
          searchLocation &&
          !isFlying &&
          sqiVisibleStations.length > 0 && (
            <>
              {sqiVisibleStations.map((entry, idx) => {
                const station = entry.station
                const am = isAmModelingStation(station)
                const nx = isNxStation(station)
                let iconKind: StationIconKind | null = null
                if (am && nx) iconKind = "nx-and-am"
                else if (nx) iconKind = "nx-only"
                else if (am) iconKind = "am-only"
                return (
                  <React.Fragment key={idx}>
                    <Marker
                      longitude={station.lon}
                      latitude={station.lat}
                      anchor="center"
                      style={{ zIndex: 10 }}
                    >
                      {iconKind ? (
                        <StationIcon kind={iconKind} />
                      ) : (
                        <div className="w-2.5 h-2.5 rounded-full bg-gray-400 border-2 border-white box-border" />
                      )}
                    </Marker>
                    <BaselineLabel
                      start={[searchLocation.lat, searchLocation.lon]}
                      end={[station.lat, station.lon]}
                      distance={entry.distKm}
                      isMetric={isMetric}
                    />
                  </React.Fragment>
                )
              })}
            </>
          )}

        {/* Debug JSON overlay — only when Debug Mode is enabled and the user has
          clicked in SQI mode. Translucent, scrollable, and tucked on the left
            *below* the search bar (top-20) and kept short (max-h-[40vh],
            max-w-[300px]) so it clears the search/coordinate bar above it and
            the settings button at the bottom-left. */}
        {debugMode && coverageMode === "sqi" && searchLocation && (
          <div className="absolute top-20 left-4 z-[1200] bg-white/40 backdrop-blur-sm border border-gray-300/60 rounded-lg shadow-xl p-2 text-[10px] leading-tight font-mono text-gray-800 max-w-[300px] max-h-[40vh] overflow-auto select-text">
            <div className="flex items-center justify-between mb-1 sticky top-0 bg-white/40 backdrop-blur-sm">
              <span className="uppercase tracking-wider font-semibold text-gray-600">
                SQI debug
              </span>
              {debugLoading && <span className="text-gray-500">loading…</span>}
            </div>
            {debugError ? (
              <pre className="text-red-700 whitespace-pre-wrap">
                {debugError}
              </pre>
            ) : (
              <pre className="whitespace-pre-wrap">
                {debugJson === null ? "" : JSON.stringify(debugJson, null, 2)}
              </pre>
            )}
          </div>
        )}

        {debugMode && coverageMode === "sqi" && showCoverage && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1200] bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-md px-3 py-2 w-[320px] max-w-[calc(100%-2rem)]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                Show coverage with SQI ≥
              </span>
              <span className="text-xs font-bold text-swift-orange">
                {sqiDebugClipThreshold}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={sqiDebugClipThreshold}
              onChange={(e) =>
                setSqiDebugClipThreshold(parseInt(e.target.value, 10))
              }
              aria-label="SQI clip threshold"
              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-swift-orange"
            />
          </div>
        )}

        {/* SQI score badge floating above the search/clicked pin. */}
        {searchLocation && coverageMode === "sqi" && sqiAtSearch !== null && (
          <Marker
            longitude={searchLocation.lon}
            latitude={searchLocation.lat}
            anchor="bottom"
            offset={[0, -40]}
            style={{ zIndex: 11 }}
          >
            <div className="px-2 py-1 rounded-md shadow-md border border-black/15 text-xs font-semibold whitespace-nowrap bg-white text-gray-800">
              <span className="text-gray-500 mr-1">SQI</span>
              <span>{sqiAtSearch < 0 ? "no coverage" : sqiAtSearch}</span>
              <span
                aria-hidden
                className="inline-block ml-1 align-middle w-2.5 h-2.5 rounded-sm border border-black/15"
                style={{
                  background:
                    sqiAtSearch < 0
                      ? "transparent"
                      : sqiToCssColor(sqiAtSearch),
                }}
              />
            </div>
          </Marker>
        )}

        {/* Search Location Pin */}
        {searchLocation && (
          <Marker
            longitude={searchLocation.lon}
            latitude={searchLocation.lat}
            anchor="bottom"
            style={{ zIndex: 10 }}
          >
            <div
              style={{
                color: "#FF8300",
                filter: "drop-shadow(0px 3px 6px rgba(0,0,0,0.4))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="currentColor"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                <circle cx="12" cy="10" r="3" fill="white" stroke="none" />
              </svg>
            </div>
          </Marker>
        )}
      </Map>
    </div>
  )
}

export default MapContent
